import { PlusCircleOutlined } from '@ant-design/icons';
import { Button, Col, Divider, Row, Space, Typography } from 'antd';
import { useFormikContext } from 'formik';
import { isNil, isNumber } from 'lodash/fp';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { type FormValues } from '.';
import IntegrationParameterFieldArray from '../../../../components/fields/IntegrationParameterFieldArray';
import CollapsibleWrapper, { Panel } from '../../../../components/wrappers/CollapsibleWrapper';
import FormFields from '../../../../themes/admin/Fields/FormFields';
import { StyledExtraConfigurationTableHeader } from '../../../shared/AdminSharedUI';

const { Title } = Typography;

const colSpan = { lg: 8, md: 12, xs: 24 };

export type DealerIntegrationDetailsProps = {
    disabled?: boolean;
    hasAutoplay?: boolean;
};

const DealerIntegrationDetails = ({ disabled, hasAutoplay }: DealerIntegrationDetailsProps) => {
    const { t } = useTranslation('dealerForm');

    const {
        values: { dealerInput },
        setFieldValue,
    } = useFormikContext<FormValues>();

    const addMoreIntegrationParameter = useCallback(() => {
        setFieldValue('dealerInput.integrationDetails.additionalParameter', [
            ...dealerInput.integrationDetails.additionalParameter,
            { purpose: '', value: '' },
        ]);
    }, [dealerInput, setFieldValue]);

    const autoplayRequired = useMemo(
        () =>
            (!isNil(dealerInput.integrationDetails.autoplay?.dealerId) &&
                !isNil(dealerInput.integrationDetails.autoplay?.yardId)) ||
            isNumber(dealerInput.integrationDetails.autoplay?.dealerId) ||
            isNumber(dealerInput.integrationDetails.autoplay?.yardId),
        [dealerInput.integrationDetails.autoplay]
    );

    return (
        <Space direction="vertical" style={{ width: '100%' }}>
            <CollapsibleWrapper defaultActiveKey={t('dealerForm:integrationTitle')}>
                <Panel key={t('dealerForm:integrationTitle')} header={t('dealerForm:integrationTitle')}>
                    <Row gutter={14}>
                        <Col {...colSpan}>
                            <FormFields.InputField
                                {...t('dealerForm:fields.dealerInput.integrationDetails.dealerCode', {
                                    returnObjects: true,
                                })}
                                disabled={disabled}
                                name="dealerInput.integrationDetails.dealerCode"
                            />
                        </Col>
                        <Col {...colSpan}>
                            <FormFields.InputField
                                {...t('dealerForm:fields.dealerInput.integrationDetails.partnerNumber', {
                                    returnObjects: true,
                                })}
                                disabled={disabled}
                                name="dealerInput.integrationDetails.partnerNumber"
                            />
                        </Col>
                        <Col {...colSpan}>
                            <FormFields.InputField
                                {...t('dealerForm:fields.dealerInput.integrationDetails.assortment', {
                                    returnObjects: true,
                                })}
                                disabled={disabled}
                                name="dealerInput.integrationDetails.assortment"
                            />
                        </Col>
                        {hasAutoplay && (
                            <>
                                <Col {...colSpan}>
                                    <FormFields.InputNumberField
                                        {...t('dealerForm:fields.dealerInput.integrationDetails.autoplay.dealerId', {
                                            returnObjects: true,
                                        })}
                                        disabled={disabled}
                                        name="dealerInput.integrationDetails.autoplay.dealerId"
                                        required={autoplayRequired}
                                    />
                                </Col>
                                <Col {...colSpan}>
                                    <FormFields.InputNumberField
                                        {...t('dealerForm:fields.dealerInput.integrationDetails.autoplay.yardId', {
                                            returnObjects: true,
                                        })}
                                        disabled={disabled}
                                        name="dealerInput.integrationDetails.autoplay.yardId"
                                        required={autoplayRequired}
                                    />
                                </Col>
                            </>
                        )}
                    </Row>
                    <Divider />
                    <StyledExtraConfigurationTableHeader>
                        <Title level={5} style={{ fontSize: '14px' }}>
                            {t('dealerForm:dealerIntegrationDetails')}
                        </Title>
                        {!disabled && (
                            <Button icon={<PlusCircleOutlined />} onClick={addMoreIntegrationParameter} type="link">
                                {t('dealerForm:actions.addIntegrationDetails')}
                            </Button>
                        )}
                    </StyledExtraConfigurationTableHeader>
                    <IntegrationParameterFieldArray
                        disabled={disabled}
                        name="dealerInput.integrationDetails.additionalParameter"
                    />
                </Panel>
            </CollapsibleWrapper>
        </Space>
    );
};

export default DealerIntegrationDetails;
